import React, { useMemo, useRef, useState, useCallback } from 'react';
import { Button, Checkbox, Dropdown, Input } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ResolveMultiSelectOption from './components/ResolveMultiSelctOption';
import { getIsMixed } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/DataRoomDocumentDrawers/ManageFileAccess/utils';
import InfiniteScroll from 'components/InfiniteScroll';
import XcircleIcon from 'icons/Xcircle.icon';
import { checkIsSelected, getSearchStringFromOption } from './utils';
import { ResolveMultiSelectProps } from './types';
import useCallbackRef from 'hooks/useCallbackRef';
import utils, { includesIgnoreCase, isNull } from 'utils';
import SearchIcon from 'icons/Search.icon';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, ENTER_KEY } from 'data/global';

import './ResolveMultiSelect.styles.scss';

function ResolveMultiSelect<T>(props: ResolveMultiSelectProps<T>) {
  const { t } = useTranslation();
  const {
    className,
    triggerIcon,
    value,
    disabled,
    options,
    hasError,
    renderAsPortal,
    hasCheckbox,
    hasSelectedOptions = true,
    hideSelected = false,
    searchable = true,
    searchPlaceholder = `${t('search')}...`,
    noDataTitle = t('noDataRMSTitle'),
    onScroll,
    onChange,
  } = props;
  const [dropdown, dropdownRef] = useCallbackRef();
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState<number>(null);
  const takeHoverInConsideration = useRef(false);

  const classes = classNames(
    'resolve-multi-select',
    { 'resolve-multi-select--error': hasError, 'as-portal': renderAsPortal },
    className,
  );

  const handleDelete = useCallback(
    (val: T) => {
      onChange(value.filter((el) => el !== val));
    },
    [onChange, value],
  );

  const handleAdd = useCallback(
    (optVal: T) => {
      if (value?.every((val) => val !== optVal)) {
        onChange([...value, optVal]);
      } else {
        onChange(value?.filter((val) => val !== optVal));
      }
    },
    [onChange, value],
  );

  const renderSelectedOptions = useMemo(
    () =>
      value?.length && hasSelectedOptions ? (
        <ul>
          {value?.map((val) => {
            const { label: optionLabel, value: optionValue } = options.find(
              (opt) => opt.value === val,
            ) || { label: '', value: null };

            return (
              <li key={String(val)}>
                <ResolveMultiSelectOption
                  label={optionLabel}
                  value={optionValue}
                  disabled={disabled}
                  onDelete={handleDelete}
                />
              </li>
            );
          })}
        </ul>
      ) : null,
    [disabled, handleDelete, hasSelectedOptions, options, value],
  );

  const searchOptions = useMemo(() => {
    if (!searchable) return null;
    if (!search) return options;

    return options.filter((option) => {
      const searchValue = getSearchStringFromOption(option);

      return includesIgnoreCase(searchValue, search);
    });
  }, [options, search, searchable]);

  const finalOptions = useMemo(
    () => (!searchable ? options : searchOptions) || [],
    [options, searchOptions, searchable],
  );

  const indexChange = useCallback(
    (value: number) => {
      if (value >= finalOptions.length) {
        setActiveIndex(0);
        return;
      }
      if (value <= 0) {
        setActiveIndex(finalOptions.length - 1);
        return;
      }

      setActiveIndex(value);
    },
    [finalOptions],
  );

  const onOptionKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLDivElement>) => {
      switch (ev.key) {
        case ENTER_KEY:
          ev.preventDefault();

          if (!isNull(activeIndex)) handleAdd(options[activeIndex].value);
          return;
        case ARROW_DOWN_KEY:
          ev.preventDefault();

          indexChange(activeIndex + 1);
          return;
        case ARROW_UP_KEY:
          ev.preventDefault();

          indexChange(activeIndex - 1);
          return;
        default:
          return null;
      }
    },
    [activeIndex, handleAdd, indexChange, options],
  );

  return (
    <Dropdown
      portalClassName="resolve-multi-select--portal"
      className={classes}
      ref={dropdownRef}
      onKeyDown={onOptionKeyDown}
      trigger={
        <div className="resolve-multi-select__trigger">
          {triggerIcon && utils.renderIcon(triggerIcon)}
          <div className="resolve-multi-select__trigger__content">
            {renderSelectedOptions}
            {searchable && (
              <Input
                className="resolve-multi-select__trigger__content__input"
                placeholder={searchPlaceholder}
                prefixNode={<SearchIcon />}
                value={search}
                disabled={disabled}
                onClick={(e) => {
                  if (search && dropdown?.isOpen) e.stopPropagation();
                }}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            )}
          </div>

          {value?.length > 0 && (
            <Button
              variant="link"
              className="delete"
              aria-label="Remove all"
              icon={XcircleIcon}
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
            />
          )}
        </div>
      }
      onClose={() => setActiveIndex(null)}
      onClick={() => {
        if (disabled) dropdown.setIsOpen(false);
      }}
      renderAsPortal={renderAsPortal}
    >
      <InfiniteScroll
        role="listbox"
        localScroll
        className="resolve-multi-select__options"
        onScroll={onScroll}
        onMouseLeave={() => setActiveIndex(null)}
        onMouseMove={() => (takeHoverInConsideration.current = true)}
      >
        {finalOptions.map((opt, index) => {
          const isSelected = checkIsSelected(value, opt.value);
          const mixed = !isSelected && getIsMixed(opt.state);
          const showOption = !hideSelected || !isSelected;
          const disabled = opt.disabled;

          return (
            showOption && (
              <div
                key={String(opt.value)}
                tabIndex={-1}
                role="option"
                aria-selected={isSelected}
                className={classNames('resolve-multi-select__options__option', {
                  'resolve-multi-select__options__option--selected': isSelected,
                  'resolve-multi-select__options__option--active-index':
                    activeIndex === index,
                  'resolve-multi-select__options__option--checkbox':
                    hasCheckbox,
                  'resolve-multi-select__options__option--disabled': disabled,
                  'rendered-in-portal': renderAsPortal,
                })}
                onFocus={() => setActiveIndex(index)}
                onMouseEnter={() => {
                  if (takeHoverInConsideration.current) {
                    takeHoverInConsideration.current = false;
                    setActiveIndex(index);
                  }
                }}
                {...(!hasCheckbox &&
                  !disabled && { onClick: () => handleAdd(opt.value) })}
              >
                {hasCheckbox ? (
                  <Checkbox
                    label={opt.label}
                    checked={isSelected}
                    disabled={disabled}
                    className={classNames({ mixed })}
                    onChange={() => handleAdd(opt.value)}
                  />
                ) : (
                  opt.label
                )}
              </div>
            )
          );
        })}

        {!finalOptions.length && search && (
          <div className="resolve-multi-select__empty">
            {t('noDataFor')} <span>{search}</span>
          </div>
        )}

        {!finalOptions.length && !search && (
          <div className="resolve-multi-select__empty">{noDataTitle}</div>
        )}
      </InfiniteScroll>
    </Dropdown>
  );
}

export default ResolveMultiSelect;
